import type { LaborActiveSessionPayload, LaborSession } from "@/lib/labor/laborApi";
import { isLaborSessionTombstoned } from "@/lib/labor/laborEndedSessionTombstone";

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function numberFromUnknown(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function parseIsoTimestamp(value: unknown): string | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    const milliseconds = value > 1e12 ? value : value * 1000;
    const date = new Date(milliseconds);
    return Number.isFinite(date.getTime()) ? date.toISOString() : null;
  }
  if (typeof value !== "string" || value.trim() === "") return null;
  const ms = Date.parse(value.trim());
  return Number.isFinite(ms) ? value.trim() : null;
}

function pickLastHeartbeatAtIso(record: Record<string, unknown>): string | undefined {
  const snake = record.last_heartbeat_at;
  const camel = record.lastHeartbeatAt;
  if (typeof snake === "string" && snake.trim() !== "") return snake.trim();
  if (typeof camel === "string" && camel.trim() !== "") return camel.trim();
  return undefined;
}

/** Parses `labor.device_session` JSON from API payloads (snake_case). */
export function parseLaborSessionRow(raw: unknown): LaborSession | null {
  const record = asRecord(raw);
  if (record === null) return null;
  const id = numberFromUnknown(record.id);
  const started = parseIsoTimestamp(record.started_at);
  if (id === null || started === null) return null;
  if (isLaborSessionTombstoned(id)) return null;
  const lastHeartbeatAt = pickLastHeartbeatAtIso(record);
  const tabRaw = record.tab_id;
  const tab_id =
    typeof tabRaw === "string" && tabRaw.trim() !== ""
      ? tabRaw.trim()
      : tabRaw === null
        ? null
        : undefined;
  return {
    id,
    started_at: started,
    device_count: numberFromUnknown(record.device_count) ?? undefined,
    net_seconds: numberFromUnknown(record.net_seconds) ?? undefined,
    page: typeof record.page === "string" ? record.page : undefined,
    ...(tab_id !== undefined ? { tab_id } : {}),
    ...(lastHeartbeatAt !== undefined ? { last_heartbeat_at: lastHeartbeatAt } : {}),
  };
}

export function openSessionFromLaborPayload(payload: LaborActiveSessionPayload | undefined): LaborSession | null {
  if (payload == null) return null;
  return parseLaborSessionRow(payload.session);
}
